import { useEffect, useRef } from "react"

// Voice coach (off by default): the phone reads each step out loud, so the
// person can keep their eyes on the car. Uses the device's own speech
// (nothing leaves the phone). The live coaching line is only spoken once it
// has held for a second, never more than every 3 s, never the same line twice
// within 6 s — guidance, not chatter.

const STABLE_MS = 1000
const MIN_GAP_MS = 3000
const NO_REPEAT_MS = 6000

export function speechAvailable(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window
}

let cachedVoice: SpeechSynthesisVoice | null = null

function pickVoice(): SpeechSynthesisVoice | null {
  if (cachedVoice) return cachedVoice
  const voices = window.speechSynthesis.getVoices()
  cachedVoice =
    voices.find((v) => v.lang === "en-US" && v.localService) ??
    voices.find((v) => v.lang === "en-US") ??
    voices.find((v) => v.lang.startsWith("en")) ??
    null
  return cachedVoice
}

// iOS only lets a page start speaking inside a user gesture; the first
// utterance unlocks the rest. Call this from the tap that opens the camera
// when the guide is already on (e.g. after a reload), so it can talk at all.
let primed = false
export function primeSpeech() {
  if (primed || !speechAvailable()) return
  primed = true
  const silent = new SpeechSynthesisUtterance(" ")
  silent.volume = 0
  window.speechSynthesis.speak(silent)
}

// Cuts off whatever is being said. The first call must come from a tap on
// iOS (see primeSpeech).
function speakNow(text: string) {
  if (!speechAvailable() || !text) return
  const synth = window.speechSynthesis
  synth.cancel()
  const utterance = new SpeechSynthesisUtterance(text)
  const voice = pickVoice()
  if (voice) utterance.voice = voice
  utterance.lang = voice?.lang ?? "en-US"
  utterance.rate = 1
  synth.speak(utterance)
}

export function useVoiceCoach({
  enabled,
  shotKey,
  intro,
  line,
}: {
  enabled: boolean
  // Changes when a new shot opens (or a retake starts).
  shotKey: string
  // What to say when the shot opens (title + instruction).
  intro: string
  // The live coaching line.
  line: string
}) {
  const lastSpokenAt = useRef(0)
  const lastSaid = useRef<{ text: string; at: number }>({ text: "", at: 0 })

  useEffect(() => {
    if (!speechAvailable()) return
    const onVoices = () => {
      cachedVoice = null
      pickVoice()
    }
    window.speechSynthesis.addEventListener?.("voiceschanged", onVoices)
    return () => window.speechSynthesis.removeEventListener?.("voiceschanged", onVoices)
  }, [])

  const say = (text: string) => {
    const now = performance.now()
    speakNow(text)
    lastSpokenAt.current = now
    lastSaid.current = { text, at: now }
  }

  // A new shot: say what to do (unless the toggle just said it).
  useEffect(() => {
    if (!enabled || !speechAvailable()) return
    const justSaid = lastSaid.current.text.includes(intro) && performance.now() - lastSaid.current.at < 1500
    if (!justSaid) say(intro)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, shotKey])

  // Live coaching, throttled. A line that has to wait (too soon after the
  // last one, or the phone is still talking) is tried again, not dropped,
  // for as long as it is still the line on screen.
  useEffect(() => {
    if (!enabled || !speechAvailable() || !line) return
    let t: ReturnType<typeof setTimeout>
    const attempt = () => {
      const now = performance.now()
      if (lastSaid.current.text === line && now - lastSaid.current.at < NO_REPEAT_MS) return
      const wait = Math.max(0, MIN_GAP_MS - (now - lastSpokenAt.current))
      if (wait > 0 || window.speechSynthesis.speaking) {
        t = setTimeout(attempt, Math.max(wait, 400))
        return
      }
      say(line)
    }
    t = setTimeout(attempt, STABLE_MS)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, line])

  // For the on/off tap: speaks now (inside the tap) and counts as said.
  return { announce: say }

  // Off, or the camera closed: stop talking at once.
  useEffect(() => {
    if (enabled || !speechAvailable()) return
    window.speechSynthesis.cancel()
  }, [enabled])
  useEffect(
    () => () => {
      if (speechAvailable()) window.speechSynthesis.cancel()
    },
    [],
  )
}
